import * as jsondiffpatch from 'jsondiffpatch';
import {Component} from '../@types/component';
import {FaultTreeNode} from '../@types/faultTreeNode';

/**
 * The parts of a Component a history entry can change, in a shape that diffs readably: nodes keyed
 * by uniqueId rather than listed by position, and linked by uniqueId rather than database id, so
 * adding a node shows as one node added instead of every later node changing.
 */
export interface ComparableComponent {
    uniqueId?: string;
    name?: string;
    description?: string;
    rootNode?: string;
    nodes: Record<string, Record<string, unknown>>;
}

// Identity and bookkeeping, which say nothing about what changed.
const IGNORED_NODE_FIELDS: ReadonlySet<string> = new Set(['id', 'containerId', 'parentId', 'childIds', 'created', 'updated']);

const differ: jsondiffpatch.DiffPatcher = jsondiffpatch.create({
    // Children are listed by uniqueId, so a reorder or insert shows as a move or an insert.
    objectHash: (item: object): string => JSON.stringify(item),
    arrays: {detectMove: true},
});

/**
 * Reduces a Component snapshot to what {@link componentDiff} compares.
 *
 * @param component the snapshot
 * @returns the comparable form
 */
export function toComparable(component: Component): ComparableComponent {
    const treeNodes: FaultTreeNode[] = component.faultTree?.faultTreeNodes ?? [];
    const keyOf = new Map<number, string>();
    const seen = new Set<string>();
    treeNodes.forEach((node: FaultTreeNode) => {
        // uniqueIds are meant to be unique within a tree; if two are not, the id tells them apart.
        const key = seen.has(node.uniqueId) ? `${node.uniqueId} (#${node.id})` : node.uniqueId;
        seen.add(key);
        keyOf.set(node.id, key);
    });

    const nodes: Record<string, Record<string, unknown>> = {};
    treeNodes.forEach((node: FaultTreeNode) => {
        const fields: Record<string, unknown> = {};
        Object.entries(node).forEach(([field, value]: [string, unknown]) => {
            if (!IGNORED_NODE_FIELDS.has(field) && value !== null && value !== undefined) {
                fields[field] = value;
            }
        });
        const children = (node.childIds ?? []).map((childId: number) => keyOf.get(childId) ?? `#${childId}`);
        if (children.length > 0) {
            fields.children = children;
        }
        nodes[keyOf.get(node.id) as string] = fields;
    });

    const rootId = component.faultTree?.rootNodeId;
    return {
        uniqueId: component.uniqueId,
        name: component.name,
        description: component.description,
        rootNode: rootId !== undefined ? keyOf.get(rootId) : undefined,
        nodes,
    };
}

/**
 * What changed between two snapshots of a Component, earlier first. Either may be missing: the
 * first entry has nothing before it, so everything in it shows as added.
 *
 * @param earlier the snapshot before the change
 * @param later   the snapshot after it
 * @returns the delta, or undefined when nothing changed
 */
export function componentDiff(
    earlier: Component | null | undefined,
    later: Component | null | undefined,
): jsondiffpatch.Delta | undefined {
    const before = earlier ? toComparable(earlier) : {};
    const after = later ? toComparable(later) : {};
    return differ.diff(before, after);
}
